import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaBookmark, FaBookOpen, FaChair, FaCheckCircle, FaDoorOpen, FaEllipsisV, FaFilter, FaPlus, FaPowerOff, FaSearch, FaTimes, FaTrashAlt, FaUserCheck } from 'react-icons/fa';
import { FiHome, FiRefreshCw } from 'react-icons/fi';
import FormSelect from '../components/FormSelect.jsx';
import { StatCard } from '../shared.jsx';
import { getLibraries, getRooms, getSeatsData } from '../services/api.js';
import axios, { getTenantSessionScope } from '../api/api1';
import { useToast } from '../context/ToastContext.jsx';
import { confirmDanger, useConfirm } from '../hooks/useConfirm.jsx';
import { useUrlFilters } from '../hooks/useUrlFilters.js';
import { DEMO_LOGIN_ENABLED } from '../demoMode.js';
import formatSeatNumber from '../utils/seatUtils.js';
import { getSeatsCache, isSeatsCacheFresh, setSeatsCache } from '../utils/seatsCache.js';
import '../styles/pages/rooms.css';

const roomIdOf = (room) => room?.room_id ?? room?.roomid ?? room?.id;
const seatRoomIdOf = (seat) => seat?.room_id ?? seat?.roomid;
const libraryIdOf = (library) => library?.id ?? library?.library_id;
const libraryNameOf = (library) => library?.library_name ?? library?.name ?? '';
const roomNameOf = (room) => room?.room_name ?? room?.roomname ?? room?.name ?? 'Room';
const roomCodeOf = (room) => room?.room_code ?? room?.code ?? '';
const roomStatusOf = (room) => String(room?.status || 'active').toLowerCase();
const seatStatusOf = (seat) => String(seat?.status || 'available').toLowerCase();
const seatCategoryOf = (seat) => String(seat?.category || seat?.type || '').toLowerCase();

const isACSeat = (seat) => {
  const category = seatCategoryOf(seat);
  return category === 'ac' || (category.includes('ac') && !category.includes('non'));
};

const isNonACSeat = (seat) => {
  const category = seatCategoryOf(seat);
  return category === 'nonac' || category === 'non_ac' || category === 'non-ac' || category.includes('non');
};

const formatFloorNumber = (floorNo) => {
  const number = Number(floorNo);
  if (!Number.isFinite(number) || number <= 0) return 'Ground Floor';
  if (number === 1) return '1st Floor';
  if (number === 2) return '2nd Floor';
  if (number === 3) return '3rd Floor';
  return `${number}th Floor`;
};

const getCurrentRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('userinfo') || '{}');
    return String(user.role || localStorage.getItem('userRole') || '').toLowerCase().replace(/[\s_-]/g, '');
  } catch {
    return '';
  }
};

const canManageRooms = (role) => DEMO_LOGIN_ENABLED || role === 'tenantowner';

const emptyRoomForm = {
  roomname: '',
  room_code: '',
  floorno: 0,
  status: 'active',
  library_id: '',
  acseats: '',
  non_acseats: '',
  capacity: '',
};

const floorOptions = Array.from({ length: 11 }, (_, index) => ({ value: index, label: formatFloorNumber(index) }));
const formStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
const filterStatusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
const seatCategoryOptions = [
  { value: 'all', label: 'Any Category' },
  { value: 'ac', label: 'AC' },
  { value: 'non_ac', label: 'Non-AC' },
];

function RoomSeatViewer({ room, seats, canManage, onClose, onDeleteSeats }) {
  const [filter, setFilter] = useState('all');
  if (!room) return null;

  const roomSeats = seats
    .filter((seat) => String(seatRoomIdOf(seat)) === String(roomIdOf(room)))
    .sort((a, b) => {
      const first = parseInt(String(a.seat_number || a.seat_no || '').replace(/\D/g, ''), 10) || 0;
      const second = parseInt(String(b.seat_number || b.seat_no || '').replace(/\D/g, ''), 10) || 0;
      return first - second;
    });

  const filteredSeats = roomSeats.filter((seat) => {
    const status = seatStatusOf(seat);
    if (filter === 'available') return status === 'available';
    if (filter === 'occupied') return status === 'occupied';
    if (filter === 'reserved') return status === 'reserved';
    if (filter === 'ac') return isACSeat(seat);
    if (filter === 'non_ac') return isNonACSeat(seat);
    return true;
  });

  const stats = {
    available: roomSeats.filter((seat) => seatStatusOf(seat) === 'available').length,
    occupied: roomSeats.filter((seat) => seatStatusOf(seat) === 'occupied').length,
    reserved: roomSeats.filter((seat) => seatStatusOf(seat) === 'reserved').length,
    acAvailable: roomSeats.filter((seat) => isACSeat(seat) && seatStatusOf(seat) === 'available').length,
    nonAcAvailable: roomSeats.filter((seat) => isNonACSeat(seat) && seatStatusOf(seat) === 'available').length,
  };

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'available', label: 'Available' },
    { value: 'occupied', label: 'Occupied' },
    { value: 'reserved', label: 'Reserved' },
    { value: 'ac', label: 'AC' },
    { value: 'non_ac', label: 'Non-AC' },
  ];

  const occupiedSeats = roomSeats.filter((seat) => seatStatusOf(seat) === 'occupied');

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section className="modal room-seat-viewer" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">ROOM SEATS</span>
            <h2>{roomNameOf(room)}</h2>
            <p className="room-modal-subtitle">{room.libraryName || 'Library'} · {formatFloorNumber(room.floor)} · {roomSeats.length} seats</p>
          </div>
          <button type="button" className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="seat-viewer-stats">
          <div><span>Available</span><b>{stats.available}</b></div>
          <div className="occupied"><span>Occupied</span><b>{stats.occupied}</b></div>
          <div className="reserved"><span>Reserved</span><b>{stats.reserved}</b></div>
          <div className="ac"><span>AC Available</span><b>{stats.acAvailable}</b></div>
          <div className="non-ac"><span>Non-AC Available</span><b>{stats.nonAcAvailable}</b></div>
        </div>

        <div className="seat-viewer-filters">
          {filters.map((item) => (
            <button key={item.value} type="button" className={filter === item.value ? 'button primary' : 'button ghost'} onClick={() => setFilter(item.value)}>{item.label}</button>
          ))}
        </div>

        <div className="seat-viewer-body">
          {!filteredSeats.length ? (
            <p className="empty-state">No seats match this filter.</p>
          ) : (
            <div className="seat-viewer-grid">
              {filteredSeats.map((seat) => {
                const status = seatStatusOf(seat);
                const category = isACSeat(seat) ? 'AC' : 'Non-AC';
                const student = seat.student_name || seat.studentname || '';
                return (
                  <div key={seat.seat_id ?? seat.seatid ?? seat.id} className={`seat-viewer-seat ${status}`} title={`${formatSeatNumber(seat.seat_number || seat.seat_no)} · ${category}${student ? ` · ${student}` : ''}`}>
                    <b>{formatSeatNumber(seat.seat_number || seat.seat_no)}</b>
                    <small>{category}</small>
                    {student && <span>{student}</span>}
                  </div>
                );
              })}
            </div>
          )}

          {occupiedSeats.length > 0 && filter !== 'available' && (
            <div className="seat-viewer-details">
              <h3>Occupied Seat Details</h3>
              <div className="table-scroll">
                <table>
                  <thead><tr><th>Seat</th><th>Type</th><th>Student</th><th>Expires</th></tr></thead>
                  <tbody>
                    {occupiedSeats.map((seat) => (
                      <tr key={seat.seat_id ?? seat.seatid ?? seat.id}>
                        <td><b>{formatSeatNumber(seat.seat_number || seat.seat_no)}</b></td>
                        <td>{isACSeat(seat) ? 'AC' : 'Non-AC'}</td>
                        <td>{seat.student_name || seat.studentname || '-'}</td>
                        <td>{seat.subscription_end || seat.subscriptionend || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="room-modal-actions">
          {canManage && <button type="button" className="button danger-ghost" onClick={() => onDeleteSeats(room)}>Remove seats</button>}
          <button type="button" className="button ghost" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}

function DeleteSeatsModal({ room, seats, saving, onClose, onDelete }) {
  const [count, setCount] = useState(1);
  const [category, setCategory] = useState('all');

  if (!room) return null;

  const availableSeats = seats.filter((seat) => {
    if (String(seatRoomIdOf(seat)) !== String(roomIdOf(room)) || seatStatusOf(seat) !== 'available') return false;
    if (category === 'ac') return isACSeat(seat);
    if (category === 'non_ac') return isNonACSeat(seat);
    return true;
  });

  const submit = (event) => {
    event.preventDefault();
    onDelete(availableSeats, Number(count), room);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section className="modal room-delete-seats" onClick={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <div><span className="eyebrow">ROOM SEATS</span><h2>Remove Seats</h2></div>
          <button type="button" className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="form-grid room-form-grid">
            <label>Category
              <FormSelect value={category} onChange={setCategory} options={seatCategoryOptions} ariaLabel="Seat category" />
            </label>
            <label>Count to Delete
              <input type="number" min="1" max={Math.max(1, availableSeats.length)} value={count} onChange={(event) => setCount(Number(event.target.value) || 1)} />
            </label>
          </div>
          <p className="room-form-note">Removes up to {availableSeats.length} available seats from {roomNameOf(room)}, starting with the highest seat numbers.</p>
          <div className="modal-actions">
            <button type="button" className="button ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="button danger" disabled={saving || !availableSeats.length}>{saving ? 'Removing...' : 'Remove Seats'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function RoomsPage({ selectedLibrary = 'All Libraries' }) {
  const [rooms, setRooms] = useState([]);
  const [seats, setSeats] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRoomId, setSavingRoomId] = useState(null);
  const [query, setQuery] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  useUrlFilters([
    { key: 'search', value: query, setValue: setQuery, defaultValue: '' },
    { key: 'library', value: libraryFilter, setValue: setLibraryFilter, defaultValue: 'all' },
    { key: 'status', value: statusFilter, setValue: setStatusFilter, defaultValue: 'all' },
  ]);
  const initialScope = useRef(selectedLibrary);
  const urlLibraryFilter = useRef(new URLSearchParams(window.location.search).has('library'));
  const [addOpen, setAddOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [viewRoom, setViewRoom] = useState(null);
  const [deleteSeatsRoom, setDeleteSeatsRoom] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [form, setForm] = useState(emptyRoomForm);
  const [refreshing, setRefreshing] = useState(false);
  const { showToast } = useToast();
  const { showConfirm, ConfirmDialog } = useConfirm();
  const currentRole = useMemo(getCurrentRole, []);
  const canManage = canManageRooms(currentRole);
  const libraryFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Libraries' },
    ...libraries.map((library) => ({ value: String(libraryIdOf(library)), label: libraryNameOf(library) })),
  ], [libraries]);
  const formLibraryOptions = useMemo(() => [
    { value: '', label: 'Select library' },
    ...libraryFilterOptions.slice(1),
  ], [libraryFilterOptions]);

  useEffect(() => {
    if (openMenuId === null) return undefined;
    const closeMenu = (event) => {
      if (event.target instanceof Element && event.target.closest('.room-menu-wrap')) return;
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [openMenuId]);

  const fetchAll = useCallback(async (forceRefresh = false) => {
    const sessionScope = getTenantSessionScope();
    const cached = getSeatsCache();
    if (!forceRefresh && isSeatsCacheFresh() && cached.rooms?.length) {
      setSeats(cached.seats || []);
      setRooms(cached.rooms || []);
      setLibraries(cached.libraries || []);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const [seatData, roomData, libraryData] = await Promise.all([
        getSeatsData(forceRefresh),
        getRooms().catch(() => []),
        getLibraries(),
      ]);
      if (sessionScope !== getTenantSessionScope()) return;
      let seatList = Array.isArray(seatData) ? seatData : (seatData?.seats || seatData?.data || []);
      let roomList = Array.isArray(roomData) ? roomData : (roomData?.rooms || roomData?.data || []);
      if (!roomList.length && Array.isArray(seatData?.rooms)) roomList = seatData.rooms;
      if (!roomList.length && Array.isArray(seatData?.room_list)) roomList = seatData.room_list;
      const seatsArr = Array.isArray(seatList) ? seatList : [];
      const roomsArr = Array.isArray(roomList) ? roomList : [];
      const libsArr = Array.isArray(libraryData) ? libraryData : [];
      setSeatsCache({ seats: seatsArr, rooms: roomsArr, libraries: libsArr }, sessionScope);
      roomsCache.current = { data: { seats: seatsArr, rooms: roomsArr, libraries: libsArr }, ts: Date.now() };
      setSeats(seatsArr);
      setRooms(roomsArr);
      setLibraries(libsArr);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not load rooms', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  useEffect(() => {
    if (urlLibraryFilter.current) {
      if (selectedLibrary === initialScope.current) return;
      urlLibraryFilter.current = false;
    }
    const allLibraries = !selectedLibrary || selectedLibrary === 'all' || selectedLibrary === 'All Libraries';
    if (allLibraries) {
      setLibraryFilter('all');
      return;
    }
    const match = libraries.find((library) => (
      String(libraryIdOf(library)) === String(selectedLibrary) ||
      String(libraryNameOf(library)).toLowerCase() === String(selectedLibrary).toLowerCase()
    ));
    setLibraryFilter(match ? String(libraryIdOf(match)) : String(selectedLibrary));
  }, [selectedLibrary, libraries]);

  const filteredRooms = useMemo(() => {
    const search = query.trim().toLowerCase();
    return rooms.map((room) => {
      const id = roomIdOf(room);
      const roomSeats = seats.filter((seat) => String(seatRoomIdOf(seat)) === String(id));
      const total = roomSeats.length || Number(room.total_seat_count ?? room.total_seats ?? room.capacity ?? 0);
      const occupied = roomSeats.length ? roomSeats.filter((seat) => seatStatusOf(seat) === 'occupied').length : Number(room.occupied_seats ?? 0);
      const reserved = roomSeats.length ? roomSeats.filter((seat) => seatStatusOf(seat) === 'reserved').length : Number(room.reserved_seats ?? 0);
      const available = roomSeats.length ? roomSeats.filter((seat) => seatStatusOf(seat) === 'available').length : Math.max(0, total - occupied - reserved);
      const acSeats = roomSeats.length ? roomSeats.filter(isACSeat).length : Number(room.ac_seats ?? room.acseats ?? 0);
      const nonAcSeats = roomSeats.length ? roomSeats.filter(isNonACSeat).length : Number(room.non_ac_seats ?? room.non_acseats ?? 0);
      const library = libraries.find((item) => String(libraryIdOf(item)) === String(room.library_id ?? room.libraryId));
      const libraryName = room.library_name || libraryNameOf(library) || '-';
      const status = roomStatusOf(room);
      return {
        ...room,
        id,
        name: roomNameOf(room),
        code: roomCodeOf(room),
        libraryName,
        status,
        floor: room.floor_no ?? room.floorno ?? 0,
        total,
        occupied,
        reserved,
        available,
        acSeats,
        nonAcSeats,
        percent: total ? Math.round((occupied / total) * 100) : 0,
      };
    }).filter((room) => {
      if (libraryFilter !== 'all') {
        const matchesLibrary = String(room.library_id ?? room.libraryId ?? '') === String(libraryFilter) ||
          String(room.libraryName).toLowerCase() === String(libraryFilter).toLowerCase();
        if (!matchesLibrary) return false;
      }
      if (statusFilter !== 'all' && room.status !== statusFilter) return false;
      if (search && !`${room.name} ${room.code} ${room.libraryName}`.toLowerCase().includes(search)) return false;
      return true;
    }).sort((first, second) => (
      first.libraryName.localeCompare(second.libraryName) ||
      first.name.localeCompare(second.name)
    ));
  }, [rooms, seats, libraries, libraryFilter, statusFilter, query]);

  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const safeCurrentPage = Math.min(currentPage, Math.max(totalPages, 1));
  const paginatedRooms = useMemo(
    () => filteredRooms.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage),
    [filteredRooms, safeCurrentPage, itemsPerPage]
  );
  const firstRoomNumber = filteredRooms.length ? (safeCurrentPage - 1) * itemsPerPage + 1 : 0;
  const lastRoomNumber = Math.min(safeCurrentPage * itemsPerPage, filteredRooms.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, libraryFilter, statusFilter, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const stats = useMemo(() => ({
    rooms: filteredRooms.length,
    active: filteredRooms.filter((room) => room.status === 'active').length,
    capacity: filteredRooms.reduce((sum, room) => sum + room.total, 0),
    reserved: filteredRooms.reduce((sum, room) => sum + room.reserved, 0),
    occupied: filteredRooms.reduce((sum, room) => sum + room.occupied, 0),
  }), [filteredRooms]);

  const libraryColorIndexes = useMemo(() => {
    const libraryKeys = [...new Set(rooms.map((room) => String(
      room.library_id ?? room.libraryId ?? room.libraryName ?? room.library_name ?? ''
    )))].sort((first, second) => first.localeCompare(second));
    return new Map(libraryKeys.map((key, index) => [key, index % 8]));
  }, [rooms]);

  const filtersActive = Boolean(query.trim()) || libraryFilter !== 'all' || statusFilter !== 'all';
  const clearFilters = () => {
    setQuery('');
    setLibraryFilter('all');
    setStatusFilter('all');
  };

  const totalSeatsInForm = (Number(form.acseats) || 0) + (Number(form.non_acseats) || 0);

  const hasDuplicateCode = (roomId = null) => {
    const code = form.room_code.trim().toLowerCase();
    if (!code) return false;
    return rooms.some((room) => (
      roomCodeOf(room).trim().toLowerCase() === code &&
      String(roomIdOf(room)) !== String(roomId)
    ));
  };

  const openCreate = () => {
    setForm({
      ...emptyRoomForm,
      library_id: libraryFilter === 'all' ? '' : libraryFilter,
    });
    setAddOpen(true);
  };

  const openEdit = (room) => {
    setForm({
      roomname: room.name,
      room_code: room.code,
      floorno: room.floor,
      status: room.status,
      library_id: room.library_id ?? room.libraryId ?? '',
      acseats: room.acSeats,
      non_acseats: room.nonAcSeats,
      capacity: room.total,
    });
    setEditingRoom(room);
  };

  const createRoom = async (event) => {
    event.preventDefault();
    if (!form.library_id) { showToast('Select a library for this room.', 'error'); return; }
    if (!form.roomname.trim()) { showToast('Room name is required.', 'error'); return; }
    if (hasDuplicateCode()) { showToast('That room code is already in use. Choose a unique code.', 'error'); return; }
    if (totalSeatsInForm < 1) { showToast('Add at least one AC or Non-AC seat.', 'error'); return; }
    setSaving(true);
    try {
      await axios.post('/api/rooms/create-with-seats', {
        roomname: form.roomname.trim(),
        room_code: form.room_code.trim(),
        floorno: Number(form.floorno) || 0,
        acseats: Number(form.acseats) || 0,
        non_acseats: Number(form.non_acseats) || 0,
        library_id: form.library_id,
        status: form.status,
      });
      setAddOpen(false);
      setForm(emptyRoomForm);
      showToast(`Room created with ${totalSeatsInForm} seats.`, 'success');
      await fetchAll(true);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not create room.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateRoom = async (event) => {
    event.preventDefault();
    if (!editingRoom) return;
    if (!form.roomname.trim()) { showToast('Room name is required.', 'error'); return; }
    if (!form.library_id) { showToast('Select a library for this room.', 'error'); return; }
    if (hasDuplicateCode(roomIdOf(editingRoom))) { showToast('That room code is already in use. Choose a unique code.', 'error'); return; }
    setSaving(true);
    try {
      await axios.put(`/api/rooms/${roomIdOf(editingRoom)}`, {
        room_name: form.roomname.trim(),
        room_code: form.room_code.trim(),
        floorno: Number(form.floorno) || 0,
        library_id: Number(form.library_id),
        capacity: Number(form.capacity) || 0,
        status: form.status,
      });
      setEditingRoom(null);
      setForm(emptyRoomForm);
      showToast('Room updated.', 'success');
      await fetchAll(true);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not update room.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (room) => {
    if (!canManage) return;
    const nextStatus = room.status === 'active' ? 'inactive' : 'active';
    setSavingRoomId(room.id);
    try {
      await axios.put(`/api/rooms/${room.id}`, { status: nextStatus });
      setRooms((current) => current.map((item) => String(roomIdOf(item)) === String(room.id) ? { ...item, status: nextStatus } : item));
      showToast(`Room ${nextStatus === 'active' ? 'activated' : 'deactivated'}.`, 'success');
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not update room status.', 'error');
    } finally {
      setSavingRoomId(null);
    }
  };

  const deleteRoom = (room) => {
    if (!canManage) return;
    if (room.occupied > 0) {
      showToast(`Move or release ${room.occupied} occupied seat${room.occupied === 1 ? '' : 's'} before deleting this room.`, 'error');
      return;
    }
    const details = [
      room.code ? `Code ${room.code}` : null,
      room.libraryName,
      formatFloorNumber(room.floor),
      `${room.total} seats`,
    ].filter(Boolean).join(' · ');
    confirmDanger(showConfirm, `Delete ${room.name}?`, `${details}. This permanently deletes the room and all of its seats. This action cannot be undone.`, async () => {
      setSavingRoomId(room.id);
      try {
        await axios.delete(`/api/rooms/${room.id}`);
        setRooms((current) => current.filter((item) => String(roomIdOf(item)) !== String(room.id)));
        setViewRoom((current) => String(roomIdOf(current)) === String(room.id) ? null : current);
        showToast('Room deleted.', 'success');
      } catch (error) {
        showToast(error?.response?.data?.error || error?.message || 'Could not delete room.', 'error');
      } finally {
        setSavingRoomId(null);
      }
    });
  };

  const deleteSeats = async (availableSeats, count, room) => {
    if (!canManage) return;
    if (!count || count < 1) { showToast('Enter a valid seat count.', 'error'); return; }
    if (availableSeats.length < count) { showToast(`Only ${availableSeats.length} available seats match this category.`, 'error'); return; }
    const selected = [...availableSeats]
      .sort((a, b) => (parseInt(String(b.seat_number || b.seat_no || '').replace(/\D/g, ''), 10) || 0) - (parseInt(String(a.seat_number || a.seat_no || '').replace(/\D/g, ''), 10) || 0))
      .slice(0, count);
    setSaving(true);
    try {
      await Promise.all(selected.map((seat) => axios.delete(`/api/seats/${seat.seat_id ?? seat.seatid ?? seat.id}`)));
      setDeleteSeatsRoom(null);
      setViewRoom((current) => String(roomIdOf(current)) === String(roomIdOf(room)) ? null : current);
      showToast(`${count} seat${count === 1 ? '' : 's'} removed.`, 'success');
      await fetchAll(true);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not remove seats.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const roomsCache = React.useRef({ data: null, ts: 0 });

  if (loading && !roomsCache.current.data && !getSeatsCache().rooms?.length) {
    return (
      <>
        <header className="dash-header rooms-page-header">
          <div className="dash-header-bar">
            <div className="rooms-header-left">
              <div className="rooms-header-icon" aria-hidden="true"><FiHome /></div>
              <div className="dash-header-info rooms-header-info">
                <div className="rooms-heading-copy">
                  <h1 className="dash-header-title">Room Management</h1>
                  <p className="dash-header-subtitle">Manage rooms, seat capacity, and availability across your libraries.</p>
                </div>
                <div className="dash-header-actions">
                  <button type="button" className="dash-action-btn" disabled aria-label="Refresh rooms">
                    <span className="dash-action-icon"><FiRefreshCw aria-hidden="true" /></span>
                    <span>Refresh</span>
                  </button>
                  {canManage && <button type="button" className="dash-action-btn primary" disabled><FaPlus aria-hidden="true" /><span>Add Room</span></button>}
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="stats-grid rooms-summary-grid">
          {[1, 2, 3, 4, 5].map((item) => (
            <div className="stat-card-skeleton" key={item}>
              <div className="skeleton-circle" />
              <div className="skeleton-stat-text">
                <div className="skeleton-text short" />
                <div className="skeleton-text" />
              </div>
            </div>
          ))}
        </div>
        <section className="panel room-filter-card" aria-label="Room filters">
          <div className="room-filter-head">
            <div className="room-filter-title">
              <span className="room-filter-icon" aria-hidden="true"><FaFilter /></span>
              <div>
                <b>Filters</b>
                <small>Refine rooms by library, status, or search term.</small>
              </div>
            </div>
            <span className="room-filter-count"><b>0</b> of <b>0</b> rooms</span>
          </div>
          <div className="room-filter-controls">
            <label className="search-wrap room-search">
              <FaSearch aria-hidden="true" />
              <input placeholder="Search room, code, or library..." disabled />
            </label>
            <div className="skeleton-select" />
            <div className="skeleton-select" />
            <button type="button" className="button ghost room-filter-clear" disabled>
              <FaTimes aria-hidden="true" />
              <span>Clear filters</span>
            </button>
          </div>
        </section>
        <div className="rooms-table-wrap rooms-table-skeleton" aria-label="Loading rooms" aria-busy="true">
          <table className="rooms-table">
            <thead>
              <tr>
                {['Sr. No.', 'Library', 'Room', 'Floor', 'Status', 'Capacity', 'Available', 'In use', 'Reserved', 'Occupancy', 'Actions'].map((heading) => (
                  <th scope="col" key={heading}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 7 }, (_, index) => (
                <tr key={index}>
                  <td><span className="rooms-table-skeleton-bar serial" /></td>
                  <td><span className="rooms-table-skeleton-bar library" /></td>
                  <td>
                    <div className="rooms-table-skeleton-room">
                      <span className="rooms-table-skeleton-bar room" />
                      <span className="rooms-table-skeleton-bar code" />
                    </div>
                  </td>
                  <td><span className="rooms-table-skeleton-bar floor" /></td>
                  <td><span className="rooms-table-skeleton-bar status" /></td>
                  <td>
                    <div className="rooms-table-skeleton-capacity">
                      <span className="rooms-table-skeleton-bar capacity" />
                      <span className="rooms-table-skeleton-bar seat-types" />
                    </div>
                  </td>
                  <td><span className="rooms-table-skeleton-bar count" /></td>
                  <td><span className="rooms-table-skeleton-bar count" /></td>
                  <td><span className="rooms-table-skeleton-bar count" /></td>
                  <td>
                    <div className="rooms-table-skeleton-occupancy">
                      <span className="rooms-table-skeleton-bar percent" />
                      <span className="rooms-table-skeleton-bar progress" />
                    </div>
                  </td>
                  <td><span className="rooms-table-skeleton-bar actions" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="dash-header rooms-page-header">
        <div className="dash-header-bar">
          <div className="rooms-header-left">
            <div className="rooms-header-icon" aria-hidden="true"><FiHome /></div>
            <div className="dash-header-info rooms-header-info">
              <div className="rooms-heading-copy">
                <h1 className="dash-header-title">Room Management</h1>
                <p className="dash-header-subtitle">Manage rooms, seat capacity, and availability across your libraries.</p>
              </div>
              <div className="dash-header-actions">
                <button type="button" className="dash-action-btn" onClick={() => fetchAll(true)} disabled={refreshing || loading} aria-label="Refresh rooms">
                  <span className={`dash-action-icon ${refreshing ? 'is-loading' : ''}`}><FiRefreshCw aria-hidden="true" /></span>
                  <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                </button>
                {canManage && <button type="button" className="dash-action-btn primary" onClick={openCreate}><FaPlus aria-hidden="true" /><span>Add Room</span></button>}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="stats-grid rooms-summary-grid">
        <StatCard icon={<FaDoorOpen />} label="Total Rooms" value={stats.rooms} tone="violet" />
        <StatCard icon={<FaCheckCircle />} label="Active Rooms" value={stats.active} tone="green" />
        <StatCard icon={<FaChair />} label="Total Capacity" value={stats.capacity} tone="pink" />
        <StatCard icon={<FaUserCheck />} label="Occupied Seats" value={stats.occupied} note={`${stats.capacity ? Math.round(stats.occupied / stats.capacity * 100) : 0}% occupancy`} tone="orange" />
        <StatCard icon={<FaBookmark />} label="Reserved Seats" value={stats.reserved} tone="orange" />
      </div>

      <div className="filter-toggle-row">
        <button type="button" className="filter-toggle-btn" aria-expanded={filtersOpen} aria-controls="room-filter-panel" onClick={() => setFiltersOpen(open => !open)}>
          <FaFilter aria-hidden="true" /><span>{filtersOpen ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>

      {filtersOpen && <section id="room-filter-panel" className="panel room-filter-card" aria-label="Room filters">
        <div className="room-filter-head">
          <div className="room-filter-title">
            <span className="room-filter-icon" aria-hidden="true"><FaFilter /></span>
            <div>
              <b>Filters</b>
              <small>Refine rooms by library, status, or search term.</small>
            </div>
          </div>
          <span className="room-filter-count"><b>{filteredRooms.length}</b> of <b>{rooms.length}</b> rooms</span>
        </div>
        <div className="room-filter-controls">
          <label className="search-wrap room-search">
            <FaSearch aria-hidden="true" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search room, code, or library..." aria-label="Search rooms" />
          </label>
          <FormSelect
            className="room-filter-select"
            value={libraryFilter}
            onChange={setLibraryFilter}
            options={libraryFilterOptions}
            icon={<FaBookOpen />}
            ariaLabel="Filter rooms by library"
          />
          <FormSelect
            className="room-filter-select"
            value={statusFilter}
            onChange={setStatusFilter}
            options={filterStatusOptions}
            icon={<FaCheckCircle />}
            ariaLabel="Filter rooms by status"
          />
          <button type="button" className="button ghost room-filter-clear" onClick={clearFilters} disabled={!filtersActive}>
            <FaTimes aria-hidden="true" />
            <span>Clear filters</span>
          </button>
        </div>
      </section>}

      {filteredRooms.length > 0 && <div className="rooms-directory">
        <div className="rooms-table-wrap">
          <table className="rooms-table">
          <caption className="rooms-table-caption">Rooms across all libraries</caption>
          <thead>
            <tr>
              <th scope="col">Sr. No.</th>
              <th scope="col">Library</th>
              <th scope="col">Room</th>
              <th scope="col">Floor</th>
              <th scope="col">Status</th>
              <th scope="col">Capacity</th>
              <th scope="col">Available</th>
              <th scope="col">In use</th>
              <th scope="col">Reserved</th>
              <th scope="col">Occupancy</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRooms.map((room, index) => (
              <tr className={openMenuId === room.id ? 'menu-open' : ''} key={room.id}>
                <td className="rooms-table-serial">{(safeCurrentPage - 1) * itemsPerPage + index + 1}</td>
                <td>
                  <span className={`rooms-library-tag color-${libraryColorIndexes.get(String(room.library_id ?? room.libraryId ?? room.libraryName)) ?? 0}`}>
                    {room.libraryName}
                  </span>
                </td>
                <td>
                  <div className="rooms-table-room">
                    <strong>{room.name}</strong>
                    {room.code && <span>{room.code}</span>}
                  </div>
                </td>
                <td>{formatFloorNumber(room.floor)}</td>
                <td>
                  <span className={`status ${room.status === 'active' ? 'good' : room.status === 'maintenance' ? 'warn' : 'muted'}`}>
                    {savingRoomId === room.id ? 'Saving' : room.status}
                  </span>
                </td>
                <td>
                  <div className="rooms-table-capacity">
                    {room.acSeats > 0 && <span className="rooms-seat-type ac">{room.acSeats} AC</span>}
                    {room.nonAcSeats > 0 && <span className="rooms-seat-type non-ac">{room.nonAcSeats} Non-AC</span>}
                    {room.acSeats === 0 && room.nonAcSeats === 0 && <span>{room.total} seats</span>}
                  </div>
                </td>
                <td><span className={`rooms-table-available ${room.available === 0 ? 'is-full' : ''}`}>{room.available}</span></td>
                <td>{room.occupied}</td>
                <td>{room.reserved}</td>
                <td>
                  <div className="rooms-table-occupancy">
                    <span>{room.percent}%</span>
                    <div className="room-progress" aria-hidden="true">
                      <span style={{ width: `${Math.min(room.percent, 100)}%`, background: room.percent > 70 ? '#f39a05' : '#5e70e8' }} />
                    </div>
                  </div>
                </td>
                <td>
                  <div className="rooms-table-actions">
                    <button type="button" className="button soft" onClick={() => setViewRoom(room)}>View seats</button>
                    {canManage && (
                      <>
                        <button type="button" className="button ghost" onClick={() => openEdit(room)}>Edit</button>
                        <div className="room-menu-wrap">
                          <button
                            type="button"
                            className="room-menu-trigger"
                            aria-label={`Actions for ${room.name}`}
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === room.id}
                            onClick={() => setOpenMenuId((current) => current === room.id ? null : room.id)}
                          >
                            <FaEllipsisV />
                          </button>
                          {openMenuId === room.id && (
                            <div className="room-menu" role="menu">
                              <button type="button" role="menuitem" onClick={() => { setOpenMenuId(null); toggleStatus(room); }} disabled={savingRoomId === room.id}>
                                <FaPowerOff /><span>{room.status === 'active' ? 'Deactivate room' : 'Activate room'}</span>
                              </button>
                              <button type="button" role="menuitem" onClick={() => { setOpenMenuId(null); setDeleteSeatsRoom(room); }}>
                                <FaChair /><span>Remove seats</span>
                              </button>
                              <div className="room-menu-divider" />
                              <button
                                type="button"
                                role="menuitem"
                                className="danger"
                                onClick={() => { setOpenMenuId(null); deleteRoom(room); }}
                                disabled={savingRoomId === room.id || room.occupied > 0}
                                title={room.occupied > 0 ? `Move ${room.occupied} occupied seats before deleting` : 'Delete room'}
                              >
                                <FaTrashAlt /><span>Delete room</span>
                              </button>
                              {room.occupied > 0 && <p className="room-menu-hint">Move {room.occupied} occupied seat{room.occupied === 1 ? '' : 's'} before deleting.</p>}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        <div className="rooms-table-pagination">
          <span className="rooms-table-page-summary">
            Showing <strong>{firstRoomNumber}–{lastRoomNumber}</strong> of <strong>{filteredRooms.length}</strong> rooms
          </span>
          <div className="rooms-table-page-controls">
            <label className="rooms-table-page-size">
              <span>Rows per page</span>
              <FormSelect
                value={itemsPerPage}
                onChange={(value) => setItemsPerPage(Number(value))}
                options={[10, 25, 50].map((value) => ({ value, label: String(value) }))}
                ariaLabel="Rows per page"
              />
            </label>
            <span className="rooms-table-page-number">Page {safeCurrentPage} of {totalPages}</span>
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage <= 1}>Previous</button>
            <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={safeCurrentPage >= totalPages}>Next</button>
          </div>
        </div>
      </div>}

      {!filteredRooms.length && (
        <div className="panel room-empty-state">
          <span className="room-empty-icon"><FaDoorOpen /></span>
          <h3>{rooms.length ? 'No rooms match your filters' : 'No rooms yet'}</h3>
          <p>{rooms.length ? 'Try a different library, status, or search term.' : 'Create your first room to start managing seats.'}</p>
          {!rooms.length && canManage && <button type="button" className="button primary" onClick={openCreate}><FaPlus aria-hidden="true" /> Add Room</button>}
        </div>
      )}

      {addOpen && (
        <div className="modal-backdrop" onClick={() => !saving && setAddOpen(false)}>
          <section className="modal room-editor" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div><span className="eyebrow">ROOM</span><h2>Add Room</h2></div>
              <button type="button" className="close-button" onClick={() => setAddOpen(false)}>×</button>
            </div>
            <form onSubmit={createRoom}>
              <div className="form-grid room-form-grid">
                <label>Library <em>*</em><FormSelect value={form.library_id} onChange={(value) => setForm({ ...form, library_id: value })} options={formLibraryOptions} icon={<FaBookOpen />} placeholder="Select library" ariaLabel="Room library" /></label>
                <label>Room Name <em>*</em><input value={form.roomname} onChange={(event) => setForm({ ...form, roomname: event.target.value })} placeholder="e.g. Main Hall" required /></label>
                <label>Room Code<input value={form.room_code} onChange={(event) => setForm({ ...form, room_code: event.target.value })} placeholder="e.g. BR-01" maxLength={20} /></label>
                <label>Floor<FormSelect value={form.floorno} onChange={(value) => setForm({ ...form, floorno: value })} options={floorOptions} ariaLabel="Room floor" /></label>
                <label>Status<FormSelect value={form.status} onChange={(value) => setForm({ ...form, status: value })} options={formStatusOptions} icon={<FaCheckCircle />} ariaLabel="Room status" /></label>
                <label>AC Seats<input type="number" min="0" value={form.acseats} onChange={(event) => setForm({ ...form, acseats: event.target.value })} placeholder="0" /></label>
                <label>Non-AC Seats<input type="number" min="0" value={form.non_acseats} onChange={(event) => setForm({ ...form, non_acseats: event.target.value })} placeholder="0" /></label>
              </div>
              <p className="room-form-hint">Use a unique code so this room is easy to identify.</p>
              <div className="room-form-note room-total-note"><span>Seats to create</span><b>{totalSeatsInForm}</b></div>
              <div className="modal-actions">
                <button type="button" className="button ghost" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="button primary" disabled={saving || totalSeatsInForm < 1}>{saving ? 'Creating...' : 'Create Room'}</button>
              </div>
            </form>
          </section>
        </div>
      )}

      {editingRoom && (
        <div className="modal-backdrop" onClick={() => !saving && setEditingRoom(null)}>
          <section className="modal room-editor" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div><span className="eyebrow">ROOM</span><h2>Edit Room</h2></div>
              <button type="button" className="close-button" onClick={() => setEditingRoom(null)}>×</button>
            </div>
            <form onSubmit={updateRoom}>
              <div className="form-grid room-form-grid">
                <label>Room Name <em>*</em><input value={form.roomname} onChange={(event) => setForm({ ...form, roomname: event.target.value })} required /></label>
                <label>Room Code<input value={form.room_code} onChange={(event) => setForm({ ...form, room_code: event.target.value })} maxLength={20} /></label>
                <label>Library <em>*</em><FormSelect value={form.library_id} onChange={(value) => setForm({ ...form, library_id: value })} options={formLibraryOptions} icon={<FaBookOpen />} placeholder="Select library" ariaLabel="Room library" /></label>
                <label>Floor<FormSelect value={form.floorno} onChange={(value) => setForm({ ...form, floorno: value })} options={floorOptions} ariaLabel="Room floor" /></label>
                <label>Capacity<input type="number" min="0" value={form.capacity} onChange={(event) => setForm({ ...form, capacity: event.target.value })} /></label>
                <label>Status<FormSelect value={form.status} onChange={(value) => setForm({ ...form, status: value })} options={formStatusOptions} icon={<FaCheckCircle />} ariaLabel="Room status" /></label>
              </div>
              <div className="modal-actions">
                <button type="button" className="button ghost" onClick={() => setEditingRoom(null)} disabled={saving}>Cancel</button>
                <button type="submit" className="button primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </section>
        </div>
      )}

      <RoomSeatViewer room={viewRoom} seats={seats} canManage={canManage} onClose={() => setViewRoom(null)} onDeleteSeats={(room) => { setViewRoom(null); setDeleteSeatsRoom(room); }} />

      {deleteSeatsRoom && (
        <DeleteSeatsModal
          room={deleteSeatsRoom}
          seats={seats}
          saving={saving}
          onClose={() => !saving && setDeleteSeatsRoom(null)}
          onDelete={deleteSeats}
        />
      )}

      <ConfirmDialog />
    </>
  );
}

export default RoomsPage;
